/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useLocation } from '@reach/router';

import defaultImg from '../../static/images/default-blog-image.jpeg';
import { articlesSchema, organizationSchema } from './structuredData';

const SEO = ({
  description, lang, meta, title, type, image, author, date
}) => {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            siteUrl
            description
            facebookID
            locale
            keywords
            organization {
              name
              url
              logo
              social
            }
            social {
              twitter
            }
          }
        }
      }
    `
  );
  const metaUrl = useLocation().href;
  const metaDescription = description || site.siteMetadata.description;
  const metaKeywords = site.siteMetadata.keywords;
  const {
    facebookID,
    locale,
    social,
    siteUrl,
    organization,
  } = site.siteMetadata;

  const schema = type === 'website'
    ? organizationSchema(organization)
    : articlesSchema(
      { ...image, src: `${siteUrl}${image.src}` },
      title,
      metaUrl,
      description,
      author,
      organization,
      date
    );

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      meta={[
        {
          name: 'description',
          content: metaDescription,
        },
        {
          name: 'keywords',
          content: metaKeywords.join(','),
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: metaDescription,
        },
        {
          property: 'og:type',
          content: type,
        },
        {
          property: 'og:url',
          content: metaUrl,
        },
        {
          property: 'og:locale',
          content: locale,
        },
        {
          property: 'og:image',
          content: `${siteUrl}${image.src}`,
        },
        {
          property: 'og:image:width',
          content: image.width,
        },
        {
          property: 'og:image:height',
          content: image.height,
        },
        {
          property: 'fb:app_id',
          content: facebookID,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:creator',
          content: `@${social.twitter}`,
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:image',
          content: `${siteUrl}${image.src}`,
        },
        {
          name: 'twitter:description',
          content: metaDescription,
        },
        {
          name: 'viewport',
          content: 'width=device-width, initial-scale=1.0, maximum-scale=1, minimum-scale=1'
        },
      ].concat(meta)}
    >
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

SEO.defaultProps = {
  author: '',
  date: {
    published: '',
    modified: '',
  },
  lang: 'en',
  type: 'website',
  meta: [],
  description: '',
  image: {
    src: defaultImg,
    height: 438,
    width: 876,
  },
};

SEO.propTypes = {
  type: PropTypes.string,
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
  author: PropTypes.string,
  date: PropTypes.shape({
    published: PropTypes.string,
    modified: PropTypes.string,
  }),
  image: PropTypes.shape({
    src: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
  }),
};

export default SEO;
